/* eslint-disable import/no-unused-modules */
import React, { useState, useCallback } from "react";
import { Flyout, FlyoutContent, FlyoutTitleRenderer } from "@amzn/stencil-react-components/flyout";
import { Row } from "@amzn/stencil-react-components/layout";
import { useChecklistArb } from "../../hooks/use-arb";
import { FirstDayHeader } from "../appointment-card/first-day-header";
import { FirstDayDetails } from "../appointment-card/first-day-details";
import { H2 } from "../typography";
import { JobDetailsModel } from "@amzn/hvh-simple-hire-checklist-internal-api-common";
import { useAppSelector } from "../../reduxStore/reduxHooks";
import { getConfigData } from "../../reduxStore/slices/configSlice";
import { GoogleMapLinkWithDirectionButton } from "../google-map-link";
import { isVirtualJob, getProcessedJCAddress } from "../../utility/jc-response-helper";
import { AddToCalendar } from "../appointment-card/add-to-calendar";
import { ButtonSize } from "@amzn/stencil-react-components/button";

/**
 * Title renderer for the flyout component
 */
const titleRenderer: FlyoutTitleRenderer = ({ titleText }) => (
  <H2 id="modal-title" color={"#FFFFFF"}>
    {titleText}
  </H2>
);

/**
 * Props for the StartDateDetails component
 */
interface StartDateDetailsProps {
  jobDetails?: JobDetailsModel;

  cssURLWithoutScheduleId?: string;
}

/**
 * Displays start date information with a details flyout
 *
 * This component is extracted from card-controlled.tsx (lines 190-247)
 * to be used in the India Hiring flow. It shows the first day header
 * and provides a flyout with detailed information about the start date.
 */
export function StartDateDetails({ jobDetails, cssURLWithoutScheduleId }: StartDateDetailsProps) {
  const { sdaData } = useAppSelector(getConfigData);
  const bundle = useChecklistArb();
  const [isStartDateDetailsViewOpen, setIsStartDateDetailsViewOpen] = useState(false);
  const vh = window.innerHeight + "px";

  // Open details view callback
  const openStartDateDetailsView = useCallback(() => {
    setIsStartDateDetailsViewOpen(true);
  }, []);

  // Close details view callback
  const closeStartDateDetailsView = useCallback(() => {
    setIsStartDateDetailsViewOpen(false);
  }, []);

  // Required data checks
  if (!jobDetails || !sdaData) return null;

  // Get necessary values for the component
  const detailsTitle = bundle.getMessage("Checklist-CardControlled-DetailsTitle");
  const startDayOnSiteTitle = bundle.getMessage("Checklist-CardControlled-FirstDayTitle");
  const startDateTime = jobDetails?.firstDayOnSiteL10N + " " + jobDetails?.startTimeL10N;
  const address = getProcessedJCAddress(jobDetails);
  const isVirtual = isVirtualJob(jobDetails);
  const addToCalendar = bundle.formatMessage("Checklist-AppointmentCard-ButtonText3");

  // Calendar event data
  const startDayCalendarEvent = {
    title: startDayOnSiteTitle,
    startTime: startDateTime,
    location: address,
  };

  return (
    <>
      {/* First day header with key information */}
      <FirstDayHeader
        jobDetails={jobDetails}
        onViewDetailsClick={openStartDateDetailsView}
        cssURLWithoutScheduleId={cssURLWithoutScheduleId}
      />

      {/* Details flyout */}
      <Flyout
        data-testid={"first-day-details-flyout"}
        isOpen={isStartDateDetailsViewOpen}
        close={closeStartDateDetailsView}
        aria-labelledby={"modal-title"}
      >
        <div style={{ height: vh }}>
          <FlyoutContent
            titleText={detailsTitle}
            renderTitle={titleRenderer}
            buttons={[
              <Row key="details-buttons" width="100%" justifyContent="space-around" alignItems="center">
                <Row gridGap="1rem">
                  <AddToCalendar
                    data-testid="start-add-to-calendar-start-day"
                    as={"Col"}
                    size={ButtonSize.Small}
                    filename={startDayOnSiteTitle}
                    event={startDayCalendarEvent}
                  >
                    {addToCalendar}
                  </AddToCalendar>
                  <GoogleMapLinkWithDirectionButton
                    isVirtual={isVirtual}
                    address={address}
                    idAddress={jobDetails.address}
                  />
                </Row>
              </Row>,
            ]}
            onCloseButtonClick={closeStartDateDetailsView}
          >
            {jobDetails && (
              <FirstDayDetails
                startDate={jobDetails.firstDayOnSite}
                address={address}
                sdaDetails={sdaData}
                isVirtual={isVirtual}
                cssURLWithoutScheduleId={cssURLWithoutScheduleId}
              />
            )}
          </FlyoutContent>
        </div>
      </Flyout>
    </>
  );
}
